// @ts-strict-ignore
/* eslint-disable no-prototype-builtins */

/**
 * @module UserNetwork
 */

import { Subject, Subscription, filter } from 'rxjs';

import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';

import { NetworkInvitation } from 'rainbow-web-sdk/models/invitation.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { Service } from 'rainbow-web-sdk/services/service';
import xmlParser from 'rainbow-web-sdk/utils/xmlParser';
import { allSettled } from 'rainbow-web-sdk/utils/allSettled';
import { User } from 'rainbow-web-sdk/models';

/** @internal */
export const USER_NETWORK_SVC = 'UserNetworkService';

/** @eventProperty */
export enum UserNetworkServiceEvents {

    /**
     * @eventProperty
     * This RBEvent is send when a new network invitation is received from another Rainbow user
     * with data {invitation:Invitation, user:User}
     */
    RAINBOW_ON_INVITATION_RECEIVED = 'RAINBOW_ON_INVITATION_RECEIVED',

    /**
     * @eventProperty
     * This RBEvent is send when the invitation (from / to) contact is accepted
     * with data {invitation:Invitation, user:User}
     */
    RAINBOW_ON_INVITATION_ACCEPTED = 'RAINBOW_ON_INVITATION_ACCEPTED',

    /**
     * @eventProperty
     * This RBEvent is send when the invitation list (received/send) is updated
     */
    RAINBOW_ON_INVITATIONS_LIST_UPDATED = 'RAINBOW_ON_INVITATIONS_LIST_UPDATED'
}

export interface UserNetworkService {
    /**
     * Subscribe to the invitations updates; All events send to the callback are of type RB Event
     * Events that will be received in the subscribe :
     *  - 'RAINBOW_ON_INVITATION_RECEIVED' with data {invitation:Invitation, contact:Contact}: A new invitation from 'contact' is received
     *  - 'RAINBOW_ON_INVITATION_ACCEPTED' with data {invitation:Invitation, contact:Contact}: The invitation (from / to) contact is accepted
     *  - 'RAINBOW_ON_INVITATIONS_LIST_UPDATED' with no data: The invitation list (received/send) is updated
     * @param handler - The callback function that will be called when events are received
     * @param eventNames - EventName, or list of EventName to subscribe to
    */
    subscribe(handler: (event: RBEvent<UserNetworkServiceEvents>) => any, eventNames?: UserNetworkServiceEvents | UserNetworkServiceEvents[]): Subscription;

    /**
     * Returns a list of users that are in the network of the connected user
     * @param includePersonnalDirectoryUsers - f true, the API will return all users in the user's network including all his personnal directory contacts
     * @returns an array of users found or an empty array if no user has been found
     */
    getUsers(includePersonnalDirectoryUsers?: boolean): User[];

    /**
     * Remove given user from our network. This means that the user will no longer receive any presence updates for this user etc.
     * @param user - the contact to be removed from our network
     */
    removeUser(user: User): Promise<void>;

    /**
     * Returns the list of received invitations for this user
     */
    getReceivedInvitations(): NetworkInvitation[];

    /**
     * Returns the list of send invitations
     */
    getSentInvitations(): NetworkInvitation[];

    /**
     * API used to invite a Rainbow user to join our network. In the case of same company, the invitation will be auto-accepted by the distant;
     * In case of different companies, this will send an invitation and email to the distant user;
     * This API will send an 'update' event on the contact object
     * @param user - The rainbow user to invite
     * @param customMessage - A custom message that will be send to the distant user via email
     *
     */
    sendInvitation(user: User, customMessage?: string): Promise<void>;

    /**
     * API used to send invitation to join rainbow to an email.
     * @param email - The email to send the invitation
     * @param customMessage - A custom message that will be send to the distant user via email
     */
    sendInvitationByEmail(email: string, customMessage?: string): Promise<void>;

    /**
     * API used to send invitations to list of emails; Limit is 100 emails
     * @param emails - The list of emails to send the invitations; Limit is 100 emails
     */
    sendBulkInvitations(emails: string[]): Promise<void>;

    /**
     * API used to resend an invitations
     * @param invitation - The invitation to resend
     */
    reSendInvitation(invitation: NetworkInvitation): Promise<void>;

    /**
     * API used to cancel a send invitation (which is not accepted yet)
     * @param invitation - The invitation that we want to cancel
     */
    cancelInvitation(invitation: NetworkInvitation): Promise<void>;

    /**
     * API used to accept a received invitation (which is not accepted or rejected yet)
     * @param invitation - The received invitation that we want to accept
     */
    acceptInvitation(invitation: NetworkInvitation): Promise<void>;

    /**
     * API used to decline a received invitation (which is not accepted or rejected yet)
     * @param invitation - The received invitation that we want to decline
     */
    declineInvitation(invitation: NetworkInvitation): Promise<void>;
}

/** @internal */
export class UserNetworkServiceRB extends Service implements UserNetworkService {

    public static getInstance() {
        return Service.create(USER_NETWORK_SVC, UserNetworkServiceRB);
    }

    public static build() {
        return new UserNetworkServiceRB();
    }

    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private contactService: ContactService;
    private settingsService: SettingsService = SettingsService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();

    private subscription: Subscription = null;
    private xmppHandler: any = null;
    private rxSubject: Subject<RBEvent<UserNetworkServiceEvents>> = new Subject<RBEvent<UserNetworkServiceEvents>>();
    private portalURL: string = null;
    private receivedInvitations: any = {};
    private sentInvitations: any = {};
    private acceptedInvitationsArray: NetworkInvitation[] = [];
    private sentInvitationsArray: NetworkInvitation[] = [];
    private receivedInvitationsArray: NetworkInvitation[] = [];
    private invitationsToUpdate: string[] = [];

    private constructor() { super(); }

    /************************************************************/
    /** LIFECYCLE STUFF                                        */
    /************************************************************/
    public start(contactService: ContactService) {
        this.contactService = contactService;
        this.logger.info('');
        this.logger.info('[invitationService] === STARTING ===');
        this.portalURL = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/`;
        this.attachHandlers();
        this.receivedInvitations = {};
        this.getAllSentInvitations();
        this.getAllReceivedInvitations();
        this.logger.info('[networkInvitationService] === STARTED ===');
    }

    public stop() {
        this.logger.info('');
        this.logger.info('[invitationService] === STOPPING ===');
        if (this.subscription) { this.subscription.unsubscribe(); }
        this.logger.info("[invitationService] === STOPPED ===");
        this.receivedInvitations = {};
    }

    public async reconnect() {
        this.attachHandlers();
        this.invitationsToUpdate = [];
        await allSettled([this.getAllReceivedInvitations(), this.getAllSentInvitations()]);
        //update the received invitations while being offline
        if (this.invitationsToUpdate.length) {
            const arrayOfPromises = [];
            this.invitationsToUpdate.forEach((id) => {
                arrayOfPromises.push(this.updateInvitationsFromServer(id));
            });

            this.invitationsToUpdate = [];

            //send update after reconnect
            await allSettled(arrayOfPromises);
            this.sendEvent(UserNetworkServiceEvents.RAINBOW_ON_INVITATIONS_LIST_UPDATED);
        }
    }

    private async updateInvitationsFromServer(invitationId: string) {
        if (invitationId) {
            const url = `${this.portalURL}${this.contactService.userContact.dbId}/invitations/${invitationId}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const invitationsData = (await response.json()).data;
            this.logger.info(`[invitationService] updateInvitationsFromServer success ${invitationId} status -- ${invitationsData.status}`);
            const userDbId = (invitationsData.invitingUserId !== this.contactService.userContact.dbId) ? invitationsData.invitingUserId : invitationsData.invitedUserId;
            if (userDbId) {
                const contact = this.contactService.getContactFromCacheById(userDbId);
                if (invitationsData.status === "declined" || invitationsData.status === "canceled") {
                    contact.ask = 'none';
                    contact.subscription = 'none';
                    contact.roster = false;
                    contact.invitation = null;
                }
                else if (invitationsData.status === "accepted" || invitationsData.status === "auto-accepted") {
                    contact.ask = 'none';
                    contact.subscription = 'both';
                    contact.roster = true;
                    contact.invitation = null;
                }
                this.contactService.updateContactRichStatus(contact);
                contact.rxSubject.next({ name: "roster", data: contact });
            }
        }
    }

    /* ********************************************************** */
    /* * OBSERVABLE STUFF                                       * */
    /* ********************************************************** */
    public subscribe(handler: (event: RBEvent<UserNetworkServiceEvents>) => any, eventNames?: UserNetworkServiceEvents | UserNetworkServiceEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<UserNetworkServiceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<UserNetworkServiceEvents>) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[UserNetworkService] subscribe () -- error -- ${error.message}`); }
        });
    }

    public sendEvent(name: UserNetworkServiceEvents, data = null): void {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    /* ********************************************************** */
    /* * EVENT HANDLING STUFF                                   * */
    /* ********************************************************** */
    public attachHandlers(): void {
        this.logger.info("[invitationService] attachHandlers");
        if (this.xmppHandler) { this.xmppService.connection.deleteHandler(this.xmppHandler); this.xmppHandler = null; }
        this.xmppHandler = this.xmppService.connection.addHandler((stanza: any) => { return this.onInvitationsUpdate(stanza); }, null, "message", "management");
    }

    private onInvitationsUpdate(stanza: any): boolean {
        const userInviteElem = xmlParser(stanza).find("userinvite");
        if (userInviteElem.length) {
            const id = userInviteElem.attr("id");
            const type = userInviteElem.attr("type");
            const action = userInviteElem.attr("action");
            switch (type) {
                case "received": this.handleReceivedInvitation(id, action); break;
                case "sent": this.handleSentInvitation(id, action); break;
                default: this.logger.warn("[invitationService] onInvitationsUpdate - received unexpected type - " + type); break;
            }
        }
        return true;
    }

    private async handleReceivedInvitation(id: string, action: string) {
        this.logger.info("[invitationService] handleReceivedInvitation");

        // Handle deletion action
        if (action === 'delete') {
            delete this.receivedInvitations[id];
            this.updateReceivedInvitationsArray();
            return;
        }

        // Handle other actions
        const invitation = await this.getServerInvitation(id);
        let updateInvitation = null;
        let status = "none";

        switch (invitation.status) {
            case "pending":
                this.receivedInvitations[invitation.id] = invitation;
                updateInvitation = invitation;
                status = "ask";
                if (invitation.invitingUserId) {
                    const contact = await this.contactService.getContactByDBId(invitation.invitingUserId, true);
                    await this.updateContactInvitationStatus(invitation.invitingUserId, status, updateInvitation);
                    this.sendEvent(UserNetworkServiceEvents.RAINBOW_ON_INVITATION_RECEIVED, { invitation, contact });
                }
                break;
            case "accepted":
            case "auto-accepted":
                this.receivedInvitations[invitation.id] = invitation;
                if (invitation.invitingUserId) {
                    const contact = await this.contactService.getContactByDBId(invitation.invitingUserId, true);
                    await this.updateContactInvitationStatus(invitation.invitingUserId, status, updateInvitation);
                    this.sendEvent(UserNetworkServiceEvents.RAINBOW_ON_INVITATION_ACCEPTED, { invitation, contact });
                }
                break;
            default:
                delete this.receivedInvitations[invitation.id];
                status = "unknown";
                if (invitation.invitingUserId) { await this.updateContactInvitationStatus(invitation.invitingUserId, status, updateInvitation); }
                break;
        }

        this.updateReceivedInvitationsArray();
    }

    private async handleSentInvitation(id: string, action: string) {
        this.logger.info("[invitationService] handleSentInvitation");

        // Handle deletion action
        if (action === "delete") {
            delete this.sentInvitations[id];
            this.updateReceivedInvitationsArray();
            return;
        }

        // Handle other actions
        const invitation = await this.getServerInvitation(id);
        let contactStatus = null;

        switch (invitation.status) {
            case "pending":
                this.sentInvitations[invitation.id] = invitation; contactStatus = "wait";
                if (invitation.invitedUserId) {
                    await this.updateContactInvitationStatus(invitation.invitedUserId, contactStatus, invitation);
                }
                break;
            case "accepted":
            case "auto-accepted":
                if (invitation.invitedUserId) {
                    const contact = await this.contactService.getContactByDBId(invitation.invitedUserId, true);
                    await this.updateContactInvitationStatus(invitation.invitedUserId, contactStatus, invitation);
                    this.sendEvent(UserNetworkServiceEvents.RAINBOW_ON_INVITATION_ACCEPTED, { invitation, contact });
                }
                break;
            default:
                delete this.sentInvitations[invitation.id];
                contactStatus = "unknown";
                if (invitation.invitedUserId) {
                    await this.updateContactInvitationStatus(invitation.invitedUserId, contactStatus, invitation);
                }
                break;
        }

        this.updateSentInvitationsArray();
    }

    private updateReceivedInvitationsArray() {
        // Handle received invitations
        this.receivedInvitationsArray = [];
        this.acceptedInvitationsArray = [];
        for (const key in this.receivedInvitations) {
            if (this.receivedInvitations.hasOwnProperty(key)) {
                const invitation = this.receivedInvitations[key];
                switch (invitation.status) {
                    case "pending": this.receivedInvitationsArray.push(invitation); break;
                    case "accepted": case "auto-accepted": this.acceptedInvitationsArray.push(invitation); break;
                    default: break;
                }
            }
        }

        // Handle received invitations
        this.receivedInvitationsArray.sort(this.sortInvitationArray);
        this.acceptedInvitationsArray.sort(this.sortInvitationArray);

        this.sendEvent(UserNetworkServiceEvents.RAINBOW_ON_INVITATIONS_LIST_UPDATED);
    }

    private updateSentInvitationsArray() {
        this.sentInvitationsArray = [];
        for (const key in this.sentInvitations) {
            if (this.sentInvitations.hasOwnProperty(key)) { this.sentInvitationsArray.push(this.sentInvitations[key]); }
        }
        this.sentInvitationsArray.sort(this.sortInvitationArray);
        this.sendEvent(UserNetworkServiceEvents.RAINBOW_ON_INVITATIONS_LIST_UPDATED);
    }

    private async getServerInvitation(invitationId: string): Promise<NetworkInvitation> {
        try {
            const url = `${this.portalURL}${this.contactService.userContact.dbId}/invitations/${invitationId}`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const invitationData = (await response.json()).data;
            this.logger.info("[invitationService] getServerInvitation success");
            return NetworkInvitation.createFromData(invitationData);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getServerInvitation');
            this.logger.error(`[invitationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /* ********************************************************** */
    /* * PRIVATE METHODS                                        * */
    /* ********************************************************** */
    private async updateContactInvitationStatus(contactDBId: string, status: string, invitation: NetworkInvitation = null) {
        const contact = await this.contactService.getContactByDBId(contactDBId);
        switch (status) {
            case "ask": contact.status = "unknown"; contact.ask = "ask"; contact.invitation = invitation; break;
            case "wait": contact.status = "wait"; contact.ask = "subscribe"; contact.invitation = invitation; break;
            default: contact.ask = "none"; contact.invitation = null; break;
        }
        this.contactService.updateContactRichStatus(contact);
        contact.rxSubject.next({ name: "update", data: contact });
    }

    private sortInvitationArray(invitA: NetworkInvitation, invitB: NetworkInvitation) {
        return new Date(invitB.lastNotificationDate).getTime() - new Date(invitA.lastNotificationDate).getTime();
    }

    private async getAllReceivedInvitations() {
        try {
            const url = `${this.portalURL}${this.contactService.userContact.dbId}/invitations/received?format=full&status=pending&limit=500`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const invitationsData = (await response.json()).data;
            this.logger.info(`[invitationService] getAllReceivedInvitations success (find ${invitationsData.length} invitations)`);

            //check for changes since the last time we called this API
            Object.keys(this.receivedInvitations).forEach((key) => {
                const receivedInvitation: NetworkInvitation = this.receivedInvitations[key];
                const newInvitationData = invitationsData.find(data => data.id === receivedInvitation.id);
                //invitation does not exist anymore on server, remove it
                if (!newInvitationData) {
                    this.logger.info(`[invitationService] received invitation no longer exist on server, we should update the contact ${key}`);
                    this.invitationsToUpdate.push(receivedInvitation.id);
                }
            });

            this.receivedInvitations = {};

            invitationsData.forEach((invitationData: any) => {
                if (invitationData.status === "pending" && invitationData.type !== "registration") {
                    const invitation = NetworkInvitation.createFromData(invitationData);
                    this.receivedInvitations[invitationData.id] = invitation;
                    if (invitationData.invitingUserId) {
                        this.updateContactInvitationStatus(invitationData.invitingUserId, "ask", invitation);
                    }
                }
                else if (invitationData.status === "accepted" || invitationData.status === "auto-accepted") {
                    this.receivedInvitations[invitationData.id] = NetworkInvitation.createFromData(invitationData);
                }
            });
            this.updateReceivedInvitationsArray();

            return this.receivedInvitations;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getAllReceivedInvitations');
            this.logger.error(`[invitationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    private async getAllSentInvitations() {
        try {
            const url = `${this.portalURL}${this.contactService.userContact.dbId}/invitations/sent?format=full&status=pending&limit=500`;
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const invitationsData = (await response.json()).data;
            this.logger.info("[invitationService] getAllSentInvitations success (find " + invitationsData.length + " invitations)");

            //check for changes since the last time we called this API
            Object.keys(this.sentInvitations).forEach((key) => {
                const sentInvitation: NetworkInvitation = this.sentInvitations[key];
                const newInvitationData = invitationsData.find(data => data.id === sentInvitation.id);
                //invitation does not exist anymore on server, remove it
                if (!newInvitationData) {
                    this.logger.info(`[invitationService] received invitation no longer exist on server, we should update the contact ${key}`);
                    this.invitationsToUpdate.push(sentInvitation.id);
                }
            });

            this.sentInvitations = {};
            invitationsData.forEach((invitationData: any) => {
                if (invitationData.status === "pending" && !invitationData.inviteToJoinMeeting) {
                    const sentInvitation = NetworkInvitation.createFromData(invitationData);
                    this.sentInvitations[invitationData.id] = sentInvitation;
                    if (sentInvitation.invitedUserId !== undefined) {
                        this.updateContactInvitationStatus(sentInvitation.invitedUserId, "wait", sentInvitation);
                    }
                }
            });
            this.updateSentInvitationsArray();
            return this.sentInvitations;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getAllSentInvitations');
            this.logger.error(`[invitationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    // **********************************************************
    // * PUBLIC METHODS USED BY CONTACT SERVICE                 *
    // **********************************************************

    /**
     * Returns a list of users that are in the network of the connected user
     * @param includePersonalDirectoryUsers - If true, the API will return all users in the user's network including all his personnal directory contacts
     * @returns an array of users found or an empty array if no user has been found
     */
    public getUsers(includePersonalDirectoryUsers: boolean = false): User[] {
        return this.contactService.getContacts().filter((user) => {
            if (user.personalDirectoryFlag) { return includePersonalDirectoryUsers; }
            return (user.id !== this.contactService.userContact.id && user.roster && user.displayName && !user.isBot && !user.isTerminated);
        });
    }

    /**
     * Remove given user from our network. This means that the user will no longer receive any presence updates for this user etc.
     * @param user - the contact to be removed from our network
     * @returns Resolve if success
     */
    public async removeUser(user: User) {
        try {
            if (!user?.dbId) { throw new Error("[contactService] removeNetworkUser : wrongContact"); }
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/networks/${user.dbId}`;
            const response: Response = await fetch(url, { method: 'DELETE', headers: this.authService.getRequestHeader() });
            if (!response.ok) throw (response);
            await response.json();
            this.logger.info(`[contactService] removeNetworkUser ${user.dbId} -- SUCCESS`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'removeNetworkUser');
            this.logger.error(errorInfo.message);
            throw errorInfo.error;
        }
    }

    /* *
    * GET RECEIVED INVITATIONS
    * !! USE CONTACT SERVICE AS AN ENTRY POINT AND DO NOT CALL THIS API DIRECTLY
    */
    public getReceivedInvitations(): NetworkInvitation[] { return this.receivedInvitationsArray; }

    /* *
    * GET SENT INVITATIONS
    * !! USE CONTACT SERVICE AS AN ENTRY POINT AND DO NOT CALL THIS API DIRECTLY
    */
    public getSentInvitations(): NetworkInvitation[] { return this.sentInvitationsArray; }

    /* *
    * SEND INVITATION TO RAINBOW CONTACT
    * !! USE CONTACT SERVICE AS AN ENTRY POINT AND DO NOT CALL THIS API DIRECTLY
    */
    public async sendInvitation(contact: Contact, customMessage: string = ''): Promise<void> {
        try {
            this.logger.info(`[invitationService] sendInvitation contact (${contact.jid})`);
            const url = `${this.portalURL}${this.contactService.userContact.dbId}/invitations`;
            const headers = this.authService.getPostHeader();
            const data = { invitedUserId: contact.dbId, customMessage };
            const response: Response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(data) });
            if (!response.ok) { throw (response); }
            this.logger.info(`[invitationService] sendInvitation - success (${contact.jid})`);
            if (contact.status === "unknown") { this.updateContactInvitationStatus(contact.dbId, 'wait'); }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'sendInvitation');
            if (errorInfo.error.errorDetailsCode && errorInfo.error.errorDetailsCode === 409602) {
                //user is already in our roster
                contact.ask = 'none';
                contact.subscription = 'both';
                contact.roster = true;
                contact.invitation = null;
                this.contactService.updateContactRichStatus(contact);
                contact.rxSubject.next({ name: "roster", data: contact });
            } else {
                this.logger.error(`[invitationService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }
    }

    /* *
    * SEND INVITATION TO EMAIL
    * !! USE CONTACT SERVICE AS AN ENTRY POINT AND DO NOT CALL THIS API DIRECTLY
    */
    public async sendInvitationByEmail(email: string, customMessage: string = '') {
        if (!email?.length) {
            this.logger.error('[invitationService] sendInvitationByEmail: no email provided');
            const error = new Error(`missing email`);
            throw (this.errorHelperService.getErrorInfo(error, 'sendInvitationByEmail'));
        }

        try {
            this.logger.info('[invitationService] sendInvitationByEmail');
            const lang = this.settingsService.getAppliLanguageCodeForServer();
            const url = `${this.portalURL}${this.contactService.userContact.dbId}/invitations`;
            const headers = this.authService.getPostHeader();
            const data = { email, lang, customMessage };
            const response: Response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(data) });
            if (!response.ok) { throw (response); }
            this.logger.info("[invitationService] sendInvitationByEmail - success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'sendInvitationByEmail');
            this.logger.error(`[invitationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /* *
    * CANCEL SEND INVITATION
    * !! USE CONTACT SERVICE AS AN ENTRY POINT AND DO NOT CALL THIS API DIRECTLY
    */
    public async cancelInvitation(invitation: NetworkInvitation) {
        try {
            const url = `${this.portalURL}${invitation.invitingUserId}/invitations/${invitation.id}/cancel`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'POST', headers });
            if (!response.ok) { throw (response); }
            this.logger.info("[invitationService] cancelOneSendInvitation success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'cancelOneSendInvitation');
            this.logger.error(`[invitationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /* *
    * SEND INVITATIONS PAR BULK
    * LIMITED TO 100 invitations
    * * !! USE CONTACT SERVICE AS AN ENTRY POINT AND DO NOT CALL THIS API DIRECTLY
    */
    public async sendBulkInvitations(emails: any[]) {
        if (!emails.length || emails.length > 100) {
            this.logger.error('[invitationService] sendBulkInvitations mail list length not correct');
            throw new Error('sendBulkInvitations mail list length not correct');
        }
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/invitations/bulk`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ emails }) });
            if (!response.ok) { throw (response); }
            this.logger.info('[invitationService] sendBulkInvitations - success');
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'sendBulkInvitations');
            this.logger.error(`[invitationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /* *
    * ACCEPT INVITATION
    * !! USE CONTACT SERVICE AS AN ENTRY POINT AND DO NOT CALL THIS API DIRECTLY
    */
    public async acceptInvitation(invitation: NetworkInvitation) {
        try {
            const url = `${this.portalURL}${invitation.invitedUserId}/invitations/${invitation.id}/accept`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'POST', headers });
            if (!response.ok) { throw (response); }
            this.logger.info("[invitationService] acceptInvitation success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'acceptInvitation');
            if (errorInfo.error.errorDetailsCode && errorInfo.error.errorDetailsCode === 404001) {
                delete this.receivedInvitations[invitation.id];
                this.updateReceivedInvitationsArray();
                this.logger.error(`[invitationService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
            else if (errorInfo.error.errorDetailsCode && errorInfo.error.errorDetailsCode === 409605) {
                const contact = await this.contactService.getContactByDBId(invitation.invitingUserId, true);
                contact.rxSubject.next({ name: 'update' });
            } else {
                this.logger.error(`[invitationService] ${errorInfo.message}`);
                throw errorInfo.error;
            }
        }
    }

    /* *
    * DECLINE INVITATION
    * !! USE CONTACT SERVICE AS AN ENTRY POINT AND DO NOT CALL THIS API DIRECTLY
    */
    public async declineInvitation(invitation: NetworkInvitation) {
        try {
            const url = `${this.portalURL}${invitation.invitedUserId}/invitations/${invitation.id}/decline`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'POST', headers });
            if (!response.ok) { throw (response); }
            this.logger.info("[invitationService] declineInvitation success");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'declineInvitation');
            if (errorInfo.error.errorDetailsCode && errorInfo.error.errorDetailsCode === 409606) {
                delete this.receivedInvitations[invitation.id];
                this.updateReceivedInvitationsArray();
            }
            this.logger.error(`[invitationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * API used to resend an invitations
     * @param invitation - The invitation to resend
     */
    public async reSendInvitation(invitation: NetworkInvitation): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/invitations/${invitation.id}/re-send`;
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'POST', headers });
            if (!response.ok) { throw (response); }
            this.logger.info(`[invitationService] reSendInvitation ${invitation.id} success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'reSendInvitation');
            this.logger.error(`[invitationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

}
